/**
 * CostMeter — CSS-only "estimated repair exposure" meter.
 *
 * Renders a centered cost range with a horizontal gradient that scales
 * from `bl-green` (low) to `bl-rust` (high). Purely visual — no chart
 * libs, no AI, no fetched data.
 */
function fmt(usd) {
    return `$${Math.round(usd || 0).toLocaleString()}`;
}

export default function CostMeter({ range, label = "Estimated repair exposure" }) {
    if (!range) return null;
    const spread = (range.high || 0) - (range.low || 0);
    const mid = ((range.low || 0) + (range.high || 0)) / 2;
    const tier = mid >= 10000 ? "high" : mid >= 4000 ? "mid" : "low";
    // Static CSS classes (see index.css) — avoid Tailwind JIT `from-[var(...)]/<opacity>`
    // which fails to compile gradient stops when a CSS-var arbitrary value is
    // paired with an opacity modifier, leaving the bar invisible.
    const gradientCls = {
        low: "cost-meter-grad-low",
        mid: "cost-meter-grad-mid",
        high: "cost-meter-grad-high",
    }[tier];

    return (
        <div className="space-y-3" data-testid="cost-meter">
            <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                {label}
            </div>
            <div className="flex items-baseline gap-3 font-bold text-bl">
                <span className="text-3xl sm:text-4xl">{fmt(range.low)}</span>
                <span className="text-bl-muted text-xl">—</span>
                <span className="text-3xl sm:text-4xl">{fmt(range.high)}</span>
            </div>
            <div className={`h-2 rounded-full ${gradientCls}`} />
            <div className="flex justify-between text-[11px] text-bl-muted">
                <span>Low estimate</span>
                <span>{`Spread $${Math.round(spread).toLocaleString()}`}</span>
                <span>High estimate</span>
            </div>
        </div>
    );
}
